import type { ReactElement } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Button, IconButton } from "@similarweb/ui-components";
import { Box, Flex, Stack } from "@similarweb/ui-components/foundations";

import { EXTENSION_LINK } from "app/pages/sales-intelligence/constants/common";
import {
    useExtensionVersionQuery
} from "app/pages/sales-intelligence/modules/extension-management/query/queries/useExtensionVersionQuery";
import ManagementSmallCard
    from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/ManagementSmallCard";
import openExternalLink from "app/pages/sales-intelligence/modules/manage-integrations/utils/openExternalLink";
import { EXTENSION_DOCUMENTATION_URL } from "app/pages/sales-intelligence/modules/management/sub-modules/constants";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { isSalesExtensionPresent } from "app/pages/sales-intelligence/utils/extension";

import { MANAGEMENT_BUTTON_MANAGE } from "../../../../../pages/management/translations/managementKeys";

export const ExtensionCard = (): ReactElement => {
    const { translate } = useTranslate();
    const hasAccess = salesSettingsHelper.isAdmin();
    const { data: extensionVersion } = useExtensionVersionQuery();

    const isExtensionInstalled = isSalesExtensionPresent();

    const handleManage = () => {
        void siNavigator.goToExtensionManagementPage();
    };

    const cardTitle = (
        <Flex gap="4px">
            <span>{translate("si.management.extension.title")}</span>
            <span>{extensionVersion}</span>
        </Flex>
    );

    return (
        <ManagementSmallCard
            dataAutomation="browser-extension-card"
            title={cardTitle}
            message={translate("si.management.extension.section.description2")}
            onHeaderActionClick={() => openExternalLink(EXTENSION_DOCUMENTATION_URL)}
        >
            <Stack>
                {hasAccess ? (
                    <Button
                        buttonHtmlType="button"
                        type="outlined"
                        onClick={handleManage}
                        data-automation="sales-intelligence-extension-manage"
                    >
                        {translate(MANAGEMENT_BUTTON_MANAGE)}
                    </Button>
                ) : isExtensionInstalled ? (
                    <Button
                        type="primary"
                        isDisabled
                        data-automation="sales-intelligence-extension-installed"
                    >
                        {translate("si.management.extension.installed")}
                    </Button>
                ) : (
                    <Box>
                        <a
                            target="_blank"
                            href={EXTENSION_LINK}
                            rel="noreferrer"
                        >
                            <IconButton
                                type="primary"
                                iconName="download"
                                data-automation="sales-intelligence-extension-download"
                            >
                                {translate("si.common.download-extension")}
                            </IconButton>
                        </a>
                    </Box>
                )}
            </Stack>
        </ManagementSmallCard>
    );
};
